"use client";

import { c as _c } from "react/compiler-runtime";
import { CustomImage } from "@repo/design-system/components/ui/custom-image";
import { CustomLink } from "@repo/design-system/components/ui/custom-link";
import { useFilterSearch } from "@repo/design-system/hooks/use-filter-search";
import { useLocale } from "@repo/design-system/hooks/use-locale";
import { matchLocale } from "@repo/design-system/lib/middleware/handle-locale";
import { getJobAdUrlFromData } from "@repo/design-system/utils/helpers";
import { useMemo } from "react";
import { Button } from "~/components/ui/button-eon";
import { i18n } from "~/setup/locales";
import { useLanguage } from "~/translations/loader/use-language";
import type { JobExcerpt } from "~/types/job";
const PATH = "images/header/subclients/econ/";

// keyword → [image name, how many images]
// More specific keywords must come before broader ones (first match wins).
const econImages: Record<string, [string, number]> = {
  "female talent days": ["female-event", 1],
  "female excellence": ["female-excellence", 2],
  "female edition": ["female-edition", 1],
  "werkstudent it": ["werkstudent-it", 1],
  "werkstudent assistenz": ["werkstudent-assistenz", 1],
  werkstudent: ["werkstudent", 1],
  director: ["director", 1],
  "visiting consultant": ["visiting-consultant", 2],
  "senior consultant": ["senior-consultant", 2],
  "project manager": ["project-manager", 2],
  principal: ["principal", 2],
  consultant: ["consultant", 2],
  bizops: ["bizops", 3],
  "power up": ["event", 3]
};
function getEconJobImage(title: string, jobNumber: string): string {
  let imageName = "event";
  let imageCount = 3;
  for (const [keyword, [image, count]] of Object.entries(econImages)) {
    if (title.toLowerCase().includes(keyword)) {
      imageName = image;
      imageCount = count;
      break;
    }
  }

  // Deterministic pick: same job always shows the same image across re-renders
  const n = Number(jobNumber) % imageCount + 1;
  return `${PATH}${imageName}_${n}.webp`;
}
export function JoblistEcon() {
  "use no memo";

  const t = useLanguage();
  const {
    data: jobs,
    isLoading,
    isFetchingNextPage,
    fetchNextPage
  } = useFilterSearch();
  const allJobs = useMemo(() => (jobs?.pages.flatMap(page => page.jobs) || []) as JobExcerpt[], [jobs]);
  const totalHits = jobs?.pages[0].totalHits || 0;
  if (isLoading) {
    return <LoadingSkeleton />;
  }
  return <div className="flex w-full justify-center pb-4">
      <div className="flex w-full max-w-screen-xl flex-col items-center md:mx-10">
        <div className="w-full">
          <div className="mt-12 grid gap-4 md:grid-cols-2">
            {allJobs.map(job => {
            const jobLocale = matchLocale([job.data.language], i18n.locales, i18n.default);
            return <div className="relative block bg-[#F6F6F7] transition-all duration-300 hover:bg-primary" key={job.data.jobNumber}>
                  <CustomLink blank className="group relative w-full hover:text-white" href={job.data.jobBoard_link || getJobAdUrlFromData(job.data)} locale={jobLocale} passParams>
                    <div className="h-full">
                      <div className="relative aspect-video">
                        <CustomImage className="object-cover" fill src={getEconJobImage(job.data.title, job.data.jobNumber)} />
                      </div>
                      <div className="flex h-52 flex-col justify-between p-8">
                        <div className="flex">
                          <h3 className="mb-8 font-bold text-3xl text-primary group-hover:text-white" lang={job.data.language?.toLowerCase()}>
                            {job.data.title}
                          </h3>
                        </div>
                        <div className="flex">
                          <p>
                            <b>{t.jobad_details_employmentType}: </b>
                            {job.data.employmentType?.[0]}
                          </p>
                        </div>
                      </div>
                    </div>
                  </CustomLink>
                </div>;
          })}
          </div>
        </div>
        <LoadMoreButton fetchNextPage={fetchNextPage} isFetchingNextPage={isFetchingNextPage} jobsPerPage={jobs?.pages[0].jobsPerPage ?? 0} pagesLength={jobs?.pages.length ?? 0} totalHits={totalHits} />
      </div>
    </div>;
}
function LoadMoreButton(t0) {
  const $ = _c(11);
  const {
    isFetchingNextPage,
    jobsPerPage,
    pagesLength,
    totalHits,
    fetchNextPage
  } = t0;
  const t = useLanguage();
  const locale = useLocale();
  const loadedJobs = Math.min(Number(pagesLength) * Number(jobsPerPage), Number(totalHits));
  const hasMoreJobs = loadedJobs < totalHits;
  if (!hasMoreJobs) {
    return null;
  }
  let t1;
  if ($[0] !== isFetchingNextPage || $[1] !== loadedJobs || $[2] !== locale || $[3] !== t.joblist_loadMore1 || $[4] !== t.joblist_loadMore2 || $[5] !== totalHits) {
    t1 = isFetchingNextPage ? t.joblist_loadMore1 || "Loading..." : `${t.joblist_loadMore1} (${loadedJobs.toLocaleString(locale)} ${t.joblist_loadMore2} ${totalHits.toLocaleString(locale)})`;
    $[0] = isFetchingNextPage;
    $[1] = loadedJobs;
    $[2] = locale;
    $[3] = t.joblist_loadMore1;
    $[4] = t.joblist_loadMore2;
    $[5] = totalHits;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== fetchNextPage || $[8] !== isFetchingNextPage || $[9] !== t1) {
    t2 = <Button className="mt-8" disabled={isFetchingNextPage} onClick={fetchNextPage} variant="ghost">{t1}</Button>;
    $[7] = fetchNextPage;
    $[8] = isFetchingNextPage;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
}
function LoadingSkeleton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex w-full justify-center pb-4"><div className="flex w-full max-w-screen-xl items-center justify-between md:mx-10"><div className="mt-12 grid w-full gap-4 md:grid-cols-2">{Array.from({
            length: 4
          }).map(_temp2)}</div></div></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function _temp2(_, i) {
  return <div className="h-[400px] animate-pulse bg-[#F6F6F7]" key={`skeleton-${i}`} />;
}